import {
  addSeconds,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  Duration,
  eachWeekendOfInterval,
  endOfDay,
  format,
  hoursToSeconds,
  intervalToDuration,
  isAfter,
  isDate,
  isEqual,
  isPast,
  isSameDay,
  isSaturday,
  isToday,
  isValid,
  isWeekend,
  max,
  min,
  previousFriday,
  setMilliseconds,
  startOfDay,
  sub,
  subHours,
  subSeconds,
} from 'date-fns'

import Logger from 'core/utils/Logger'

export class DateHelper {
  public static readonly UTC_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'"
  public static readonly UTC_TIMEZONE_SUFFIX = 'Z'

  public static now() {
    return new Date()
  }

  public static min(...dates: Date[]) {
    return min(dates)
  }

  public static max(...dates: Date[]) {
    return max(dates)
  }

  public static startOfDay(date: Date): Date {
    return startOfDay(date)
  }

  public static endOfDay(date: Date): Date {
    return endOfDay(date)
  }

  public static addSeconds(date: Date, amount: number): Date {
    return addSeconds(date, amount)
  }

  public static subSeconds(date: Date, amount: number): Date {
    return subSeconds(date, amount)
  }

  public static subHours(date: Date, amount: number): Date {
    return subHours(date, amount)
  }

  public static isWeekend(date: Date): boolean {
    return isWeekend(date)
  }

  public static getPreviousFriday(date: Date): Date {
    return previousFriday(date)
  }

  public static isAfter(a: Date, b: Date): boolean {
    const formatedA = new Date(format(a, DateHelper.UTC_FORMAT))
    const formatedB = new Date(format(b, DateHelper.UTC_FORMAT))

    return isAfter(formatedA, formatedB)
  }

  public static isEqual(a: Date, b: Date): boolean {
    const formatedA = new Date(format(a, DateHelper.UTC_FORMAT))
    const formatedB = new Date(format(b, DateHelper.UTC_FORMAT))

    return isEqual(formatedA, formatedB)
  }

  public static isAfterOrEqual(a: Date, b: Date): boolean {
    return DateHelper.isAfter(a, b) || DateHelper.isEqual(a, b)
  }

  public static setMilliseconds(date: Date, milliseconds: number): Date {
    return setMilliseconds(date, milliseconds)
  }

  public static getIntervalToDuration(start: Date, end: Date): Duration {
    return intervalToDuration({ start, end })
  }

  public static getIntervalToHHMMSSDuration(start: Date, end: Date): THHMMSSDuration {
    return {
      hours: differenceInHours(end, start, { roundingMethod: 'floor' }),
      minutes: differenceInMinutes(end, start, { roundingMethod: 'floor' }) % 60,
      seconds: differenceInSeconds(end, start, { roundingMethod: 'floor' }) % 60,
    }
  }

  public static weekendsInSecondsBetween(start: Date, end: Date): number {
    const weekends: Date[] = eachWeekendOfInterval({ start, end })
    if (!weekends.length) {
      return 0
    }

    let seconds = 0
    let isStartWeekend = false
    if (DateHelper.isSameDay(start, weekends[0])) {
      isStartWeekend = true
      seconds = differenceInSeconds(start, DateHelper.endOfDay(start))
    }
    const isLastWeekendSaturday = isSaturday(weekends[weekends.length - 1])
    seconds += hoursToSeconds(
      (weekends.length - Number(isStartWeekend) + Number(isLastWeekendSaturday)) * 24
    )

    return seconds
  }

  public static isDate(value: unknown): value is Date {
    return isDate(value)
  }

  public static isValid(value: Date) {
    return isValid(value)
  }

  public static isPast(value: Date) {
    return isPast(value)
  }

  public static isSameDay(dateLeft: Date | number, dateRight: Date | number) {
    return isSameDay(dateLeft, dateRight)
  }

  public static isToday(value: Date) {
    return isToday(value)
  }

  /**
   * String with date format 'YYYY-MM-DDTHH:mm:ss.sssZ' or 'YYYY-MM-DDTHH:mm:ss.sss' is awaited.
   * UTC timezone suffix is added, if not presented in passed datetime string.
   * @param value
   */
  public static convertServerISOToDate(value: string) {
    let result: Date | null = value.endsWith(this.UTC_TIMEZONE_SUFFIX)
      ? new Date(value)
      : new Date(`${value}${this.UTC_TIMEZONE_SUFFIX}`)

    if (!this.isValid(result)) {
      result = null
    }

    return result
  }

  public static convertDateToServerISO(value: Date) {
    if (!this.isValid(value)) {
      return ''
    }

    return value.toISOString()
  }

  public static convertDateToServerISONoUTC(value: Date) {
    if (!this.isValid(value)) {
      return ''
    }

    return format(value, this.UTC_FORMAT)
  }

  public static trimTailTZ(value: string): string {
    let result = value
    if (value.endsWith(this.UTC_TIMEZONE_SUFFIX)) {
      result = result.substring(0, result.length - 1)
    }

    return result
  }

  public static formatToType(value: TFormatValue, type: TFormatTypes) {
    let result = ''

    try {
      const date = this.isDate(value) ? value : new Date(value)

      if (!this.isValid(date)) {
        throw new Error(`'${value}' is not a valid date`)
      }

      switch (type) {
        case 'date3':
          result = format(date, 'dd MMM yyyy') // sample 03 Feb 2020
          break
        case 'date':
          result = format(date, 'MMM do yyyy') // Mar 23th 2019
          break
        case 'full':
          result = format(date, 'MMM dd yyyy | HH:mm') // Mar 23 2019 | 18:03
          break
        case 'file':
          result = format(date, 'yyyyMMdd_HH_mm') // 20190323_18_03
          break
        default:
        // do nothing
      }
    } catch (e) {
      Logger.error(e)
    }

    return result
  }

  /**
   * String with date format 'YYYY-MM-DDTHH:mm:ss.sssZ' or 'YYYY-MM-DDTHH:mm:ss.sss' is awaited.
   * UTC timezone suffix is added, if not presented in passed datetime string.
   * @param value
   */
  public static convertISOAndFormatToFull(value: string | null) {
    return this.convertISOAndFormatToType(value, 'full')
  }

  /**
   * String with date format 'YYYY-MM-DDTHH:mm:ss.sssZ' or 'YYYY-MM-DDTHH:mm:ss.sss' is awaited.
   * UTC timezone suffix is added, if not presented in passed datetime string.
   * @param value
   */
  public static convertISOAndFormatToDate(value: string | null) {
    return this.convertISOAndFormatToType(value, 'date')
  }

  /**
   * String with date format 'YYYY-MM-DDTHH:mm:ss.sssZ' or 'YYYY-MM-DDTHH:mm:ss.sss' is awaited.
   * UTC timezone suffix is added, if not presented in passed datetime string.
   * @param value
   */
  public static convertISOAndFormatToDate3(value: string | null) {
    return this.convertISOAndFormatToType(value, 'date3')
  }

  /**
   * String with date format 'YYYY-MM-DDTHH:mm:ss.sssZ' or 'YYYY-MM-DDTHH:mm:ss.sss' is awaited.
   * UTC timezone suffix is added, if not presented in passed datetime string.
   * @param value
   */
  public static convertISOAndFormatToFile(value: string | null) {
    return this.convertISOAndFormatToType(value, 'file')
  }

  /**
   * String with date format 'YYYY-MM-DDTHH:mm:ss.sssZ' or 'YYYY-MM-DDTHH:mm:ss.sss' is awaited.
   * UTC timezone suffix is added, if not presented in passed datetime string.
   * @param value
   * @param type
   */
  public static convertISOAndFormatToType(value: string | null, type: TFormatTypes) {
    if (!value) {
      return ''
    }
    const converted = DateHelper.convertServerISOToDate(value)

    return converted ? DateHelper.formatToType(converted, type) : ''
  }

  public static formatToFull(value: TFormatValue) {
    return this.formatToType(value, 'full')
  }

  public static formatToDate3(value: TFormatValue) {
    return this.formatToType(value, 'date3')
  }

  public static formatToFile(value: TFormatValue) {
    return this.formatToType(value, 'file')
  }

  public static getYesterdayStartDate() {
    return startOfDay(sub(new Date(), { days: 1 }))
  }
}

type TFormatValue = Date | number | string
type TFormatTypes = 'date3' | 'date' | 'full' | 'file'

export type THHMMSSDuration = Required<Pick<Duration, 'hours' | 'minutes' | 'seconds'>>
